import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "cn"

// CUSTOMIZED: Soft semantic variants use the color-system recipe
// `bg-*-100 text-*-700`. `default` is cyan soft (brand-hue chrome), not solid
// `--primary`. Border on secondary. Stock outline / ghost / link kept for neutral UI.

const badgeVariants = cva(
  "group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        // Soft semantic recipe (color system)
        default: "bg-cyan-100 text-foreground [a]:hover:bg-cyan-200",
        success: "bg-success-100 text-success-700 [a]:hover:bg-success-200",
        warning: "bg-warning-100 text-warning-700 [a]:hover:bg-warning-200",
        orange: "bg-orange-100 text-orange-700 [a]:hover:bg-orange-200",
        destructive:
          "bg-destructive-100 text-destructive-700 focus-visible:ring-destructive/20 [a]:hover:bg-destructive-200",
        blue: "bg-blue-100 text-blue-700 [a]:hover:bg-blue-200",
        purple: "bg-purple-100 text-purple-700 [a]:hover:bg-purple-200",

        // Neutral / stock
        secondary:
          "bg-secondary border-gray-300 text-secondary-foreground [a]:hover:bg-secondary/80",
        outline:
          "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        ghost:
          "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

export { Badge, badgeVariants }
